import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionServices.module.css';
import config from '../../../config/config';
import { ExternalLink, NamedLink, ServiceIcon } from '../../../components';
import { configServicePages, serviceRouteName } from '../../../config/configServicePages';
import { t } from '../../../util/messages';

/**
 * Capabilities.
 *
 * Eight cards in a three-column grid, with the two AI capabilities first —
 * their placement is what carries the AI-native positioning. The ninth cell
 * would otherwise be empty, so it holds a conversion step instead.
 */


const SectionServices = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);
  const servicesConfig = config.configServices;

  return (
    <section id="services" className={classes} aria-label="Our capabilities">
      <div className={css.content}>
        <header className={css.header} data-reveal>
          <div>
            <span className={css.tag}>
              <i aria-hidden="true" />
              {t('SectionServices.eyebrow')}
            </span>
            <h2 className={css.title}>{t('SectionServices.heading')}</h2>
          </div>
          <p className={css.description}>{t('SectionServices.description')}</p>
        </header>

        <div className={css.services}>
          {servicesConfig.map((service, index) => {
            const hasPage = Boolean(configServicePages[service.id]);

            const card = (
              <>
                <ServiceIcon serviceId={service.id} className={css.icon} />
                <h3 className={css.label}>{service.title}</h3>
                <p className={css.description}>{service.description}</p>

                {service.tags?.length > 0 && (
                  <ul className={css.tags}>
                    {service.tags.map(tag => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                )}
              </>
            );

            const cardClass = classNames(css.service, { [css.serviceAi]: service.isAi });

            /*
             * A card links to the service's own page when it has one. This
             * grid is the main route into those pages, and it is where a
             * visitor reading about a capability most wants more of it.
             * Services without a page entry stay as plain articles rather
             * than linking nowhere.
             */
            return hasPage ? (
              <NamedLink
                key={service.id}
                name={serviceRouteName(service.id)}
                className={classNames(cardClass, css.serviceLink)}
                title={service.title}
                data-reveal
                data-reveal-delay={(index % 3) * 80}
              >
                {card}
              </NamedLink>
            ) : (
              <article
                key={service.id}
                className={cardClass}
                data-reveal
                data-reveal-delay={(index % 3) * 80}
              >
                {card}
              </article>
            );
          })}

          {/*
            Fills the ninth cell with a conversion step rather than a gap.

            It asks for a call, so it opens the scheduling link rather than
            the contact form — sending someone who clicked "book a call" to a
            message box is a step they did not ask for.
          */}
          <ExternalLink
            className={classNames(css.service, css.serviceCta)}
            href={
              process.env.REACT_APP_SCHEDULE_MEETING_URL ||
              'https://calendly.com/gitribe/30min'
            }
            data-reveal
            data-reveal-delay="160"
          >
            <h3 className={css.label}>{t('SectionServices.ctaTitle')}</h3>
            <p className={css.description}>{t('SectionServices.ctaDescription')}</p>
            <span className={css.ctaLink}>
              {t('SectionServices.ctaAction')}
              <svg width="15" height="11" viewBox="0 0 15 11" fill="none" aria-hidden="true">
                <path
                  d="M9.5 1L14 5.5 9.5 10M14 5.5H0"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </ExternalLink>
        </div>
      </div>
    </section>
  );
};

SectionServices.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionServices);
